/**
 * LeadManagement — API-driven lead listing, filtering, status updates.
 * All data comes from backend. No local lead creation or persistence.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatDateLocale, formatDateTimeLocale } from '../../../utils/dateFormat';
import { buildProviderTaxPayload } from '../../../utils/providerTax';
import { useTranslation } from 'react-i18next';
import { useClinicLeads } from '../../../hooks/useClinicLeads';
import { clinicLeadAPI } from '../../../utils/api';
import { useClinicScope } from '../../../hooks/useClinicScope';
import { useRBAC } from '../../../context/RBACContext';
import { useSubscription } from '../../../context/SubscriptionContext';
import { AssignmentPanel } from '../../../components/AssignmentPanel';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Users, Search, Filter, Eye, UserPlus, Clock,
  Mail, Phone, Globe, Calendar, X, ChevronLeft,
  Building2, Archive, Lock, CheckSquare, UserCheck, RefreshCw, History, Sparkles,
  Send, Upload, FileText, FileSpreadsheet
} from 'lucide-react';
import { toast as sonnerToast } from 'sonner';
import AddLeadModal from './AddLeadModal';
// BULK-LEAD-IMPORT-V1 — separate action alongside Add Patient.
import BulkLeadImport from './BulkLeadImport';

// Bridge: convert Shadcn-style toast calls to sonner
const toast = (opts) => {
  if (typeof opts === 'string') return sonnerToast(opts);
  const { title, description, variant } = opts || {};
  if (variant === 'destructive') return sonnerToast.error(title || 'Error', { description });
  return sonnerToast.success(title || 'Success', { description });
};
// B-3 — expose `.error` / `.success` so callsites that use the
// modern sonner-style API (`toast.error('...')`) don't TypeError.
toast.error = sonnerToast.error;
toast.success = sonnerToast.success;
toast.info = sonnerToast.info || sonnerToast;
// Allow the sonner-style call sites (`toast.error(msg)`) used by the
// B-3 assign error handler to work through the same bridge.
toast.error = (msg, opts) => sonnerToast.error(msg, opts);
toast.success = (msg, opts) => sonnerToast.success(msg, opts);
import OfferComposer from '../../../components/OfferComposer';
import { formatOfferError, currencyPolicyReasonKey } from '../../../utils/currencyPolicy';
import { FeatureGatedWrapper, GatedButton, TrialFeatureNotice } from '../../../components/FeatureGatedWrapper';
import api from '../../../utils/api';
import { MarkAsWonDialog } from '../../../components/MarkAsWonDialog'; // LIFECYCLE-V3.2
import { SalesStageBadge, SalesActions } from '../../../components/leads/SalesStage'; // SALES-CRM-SIMPLIFICATION
// M16B-07B — canonical Treatment Ready visibility (per-treatment-slug)
import TreatmentReadyPanel from '../../../components/treatment-ready/TreatmentReadyPanel';
import { resolveLeadOrigin, isMarketplaceLead, isProviderOwnedLead, LEAD_ORIGIN } from '../../../utils/leadOrigin'; // TREATX-LEAD-ORIGIN-DISPLAY-01

// ── Constants (display-only, from backend enum values) ───────
// The `label` field here is only a fallback if a translation lookup
// misses; live rendering routes every enum through `t('provider.leads.statuses.*')`.
const LEAD_STATUS_DISPLAY = {
  NEW: { label: 'New', color: 'bg-blue-100 text-blue-700' },
  CONTACTED: { label: 'Contacted', color: 'bg-cyan-100 text-cyan-700' },
  QUALIFIED: { label: 'Qualified', color: 'bg-indigo-100 text-indigo-700' },
  OFFER_SENT: { label: 'Offer Sent', color: 'bg-purple-100 text-purple-700' },
  NEGOTIATION: { label: 'Negotiation', color: 'bg-orange-100 text-orange-700' },
  WON: { label: 'Won', color: 'bg-green-100 text-green-700' },
  INVITED_TO_PLATFORM: { label: 'Invited', color: 'bg-teal-100 text-teal-700' },
  PATIENT_REGISTERED: { label: 'Patient Registered', color: 'bg-emerald-100 text-emerald-700' },
  PAYMENT_PENDING: { label: 'Payment Pending', color: 'bg-yellow-100 text-yellow-700' },
  PAID: { label: 'Paid', color: 'bg-green-100 text-green-800' },
  TREATMENT_IN_PROGRESS: { label: 'In Treatment', color: 'bg-blue-100 text-blue-800' },
  COMPLETED: { label: 'Completed', color: 'bg-green-200 text-green-900' },
  LOST: { label: 'Lost', color: 'bg-red-100 text-red-700' },
  ON_HOLD: { label: 'On Hold', color: 'bg-gray-100 text-gray-700' },
};
// Legacy / system-derived lead states that still exist on historical rows.
// They are queryable and must render a localized label (never the raw enum),
// but are NOT offered as user-selectable transitions (the backend
// `_ALLOWED_TRANSITIONS` map never lists them as `allowed_next`).
const LEGACY_LEAD_STATUS_DISPLAY = {
  QUOTE_SENT: { label: 'Offer Sent', color: 'bg-purple-100 text-purple-700', alias_of: 'OFFER_SENT' },
  QUOTE_EXPIRED: { label: 'Offer Expired', color: 'bg-slate-100 text-slate-600' },
  QUOTE_REVISED: { label: 'Offer Revised', color: 'bg-purple-50 text-purple-600' },
  ORDER_CREATED: { label: 'Order Created', color: 'bg-green-100 text-green-700', alias_of: 'WON' },
  DEPOSIT_PAID: { label: 'Deposit Paid', color: 'bg-green-100 text-green-800' },
  ARCHIVED: { label: 'Archived', color: 'bg-gray-100 text-gray-500' },
};
const statusLabelI18n = (t, status) => t(
  `provider.leads.statuses.${status}`,
  LEAD_STATUS_DISPLAY[status]?.label || LEGACY_LEAD_STATUS_DISPLAY[status]?.label
    || String(status || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
);
// Filter value for a canonical status: includes its legacy alias so historical rows match.

const TERMINAL_STATUSES = ['LOST', 'COMPLETED'];

const SOURCE_CHANNEL_LABELS = {
  SELF_SOURCED: 'Self-Sourced',
  SOCIAL_MEDIA: 'Social Media',
  REFERRAL: 'Referral',
  WALK_IN: 'Walk-In',
  PHONE_INQUIRY: 'Phone',
  WEBSITE: 'Website',
  EVENT: 'Event',
  PARTNER: 'Partner',
  OTHER: 'Other',
};

const LeadManagement = ({ clinicId: propClinicId, onRefresh, focusLeadId }) => {
  const { t } = useTranslation();
  const scope = useClinicScope();
  const clinicId = propClinicId || scope.clinicId;
  const { isClinicAdmin } = useRBAC();

  const {
    leads, total, loading, error, fetchLeads,
    createLead, updateStatus, sendInvite,
    uploadMedicalFile, listMedicalFiles,
    addNote, listNotes, addMessage, listMessages,
    createOffer, listOffers, getLatestOffer, assignLead,
  } = useClinicLeads(clinicId);

  // UI state
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sourceFilter, setSourceFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [topLevelSalesReps, setTopLevelSalesReps] = useState([]);

  // Subscription gating
  let canUseBulkOperations = true;
  let currentPackage = null;
  try {
    const sub = useSubscription();
    canUseBulkOperations = sub.canUseFeature('bulkOperations');
    currentPackage = sub.currentPackage;
  } catch {}

  // Load sales reps for AddLeadModal and InlineLeadControls
  useEffect(() => {
    api.get('/clinic/team')
      .then(({ data }) => {
        const reps = (data.members || []).filter(m =>
          m.role === 'provider_sales_rep' || m.role === 'provider_sales_rep'
        );
        setTopLevelSalesReps(reps);
      })
      .catch(() => {});
  }, []);

  // ── Fetch leads on mount and filter change ──────────────────
  const loadLeads = useCallback(() => {
    const params = {};
    if (sourceFilter !== 'ALL') params.source_type = sourceFilter;
    if (statusFilter !== 'ALL') params.stage = statusFilter; // SALES-CRM-SIMPLIFICATION — human bucket resolved server-side
    fetchLeads(params);
  }, [fetchLeads, sourceFilter, statusFilter]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  // UAT-MSG-VIEW-LEAD-02 — open the lead a deep link names (from Messages →
  // "View lead"). Uses the SAME detail modal the list row opens. A clinic with
  // thousands of leads will not have it on the loaded page, so it is resolved
  // through the same scoped list endpoint by id.
  const focusAppliedRef = useRef(null);
  useEffect(() => {
    if (!focusLeadId || focusAppliedRef.current === focusLeadId || !clinicId) return;
    const found = (leads || []).find(l => l.id === focusLeadId);
    if (found) {
      focusAppliedRef.current = focusLeadId;
      setSelectedLead(found);
      setShowDetailModal(true);
      return;
    }
    let cancelled = false;
    clinicLeadAPI.listLeads(clinicId, { lead_id: focusLeadId, limit: 1 })
      .then((res) => {
        const one = (res.data?.leads || [])[0];
        if (!cancelled && one) {
          focusAppliedRef.current = focusLeadId;
          setSelectedLead(one);
          setShowDetailModal(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [focusLeadId, leads, clinicId]);

  // BUGFIX: Sync selectedLead with refreshed leads data after status/assignment changes
  useEffect(() => {
    if (selectedLead && leads.length > 0) {
      const updated = leads.find(l => l.id === selectedLead.id);
      if (updated && JSON.stringify(updated) !== JSON.stringify(selectedLead)) {
        setSelectedLead(updated);
      }
    }
  }, [leads, selectedLead]);

  // ── Client-side filtering (search + terminal grouping) ─────
  const filteredLeads = React.useMemo(() => {
    let result = [...leads];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l =>
        (l.patient_name || '').toLowerCase().includes(q) ||
        (l.patient_email || '').toLowerCase().includes(q) ||
        (l.treatment_interest || '').toLowerCase().includes(q) ||
        (l.treatment_display_name || '').toLowerCase().includes(q) ||
        (l.country_of_origin || '').toLowerCase().includes(q) ||
        // PII: marketplace identities arrive masked ("S***"), so a full-name
        // query cannot match them. The lead reference is searchable instead —
        // no masking is relaxed anywhere.
        String(l.id || '').toLowerCase().includes(q)
      );
    }

    // RENEWAL-CYCLE-01 — order by the CURRENT request cycle (falls back to
    // created_at, so leads that were never renewed keep their exact order).
    result.sort((a, b) => String(b.current_cycle_started_at || b.created_at || '')
      .localeCompare(String(a.current_cycle_started_at || a.created_at || '')));
    return result;
  }, [leads, searchQuery, statusFilter]);

  // ── Stats ───────────────────────────────────────────────────
  const marketplaceCount = leads.filter(isMarketplaceLead).length;
  const providerOwnedCount = leads.filter(isProviderOwnedLead).length;
  const lostCount = leads.filter(l => l.status_transitions?.human_stage === 'LOST').length;

  // ── Handlers ────────────────────────────────────────────────
  const handleCreateLead = async (formData) => {
    await createLead(formData);
    loadLeads();
    toast({ title: t('provider.leads.mgmt.toastCreatedTitle'), description: t('provider.leads.mgmt.toastCreatedDesc', { name: formData.patient_name }) });
  };

  const handleStatusChange = async (leadId, newStatus, reason) => {
    try {
      await updateStatus(leadId, newStatus, reason);
      loadLeads();
      toast({ title: t('provider.leads.mgmt.toastStatusUpdatedTitle'), description: t('provider.leads.mgmt.toastStatusUpdatedDesc', { status: statusLabelI18n(t, newStatus) }) });
    } catch (err) {
      const detail = err.response?.data?.detail || t('provider.leads.mgmt.toastStatusFailedDefault');
      toast({ title: t('provider.leads.mgmt.toastError'), description: detail, variant: 'destructive' });
    }
  };

  const handleSendInvite = async (leadId) => {
    try {
      const result = await sendInvite(leadId);
      loadLeads();
      toast({ title: t('provider.leads.mgmt.toastInvitationSentTitle'), description: result.note || t('provider.leads.mgmt.toastInvitationSentDesc') });
    } catch (err) {
      const detail = err.response?.data?.detail || t('provider.leads.mgmt.toastInviteFailed');
      toast({ title: t('provider.leads.mgmt.toastError'), description: detail, variant: 'destructive' });
    }
  };

  const handleUploadFile = async (leadId, file, category) => {
    try {
      await uploadMedicalFile(leadId, file, category, '');
      toast({ title: t('provider.leads.mgmt.toastFileUploadedTitle'), description: file.name });
    } catch (err) {
      toast({ title: t('provider.leads.mgmt.toastUploadFailedTitle'), description: err.response?.data?.detail || t('provider.leads.mgmt.toastError'), variant: 'destructive' });
    }
  };

  // ── Selection helpers ───────────────────────────────────────
  const toggleLeadSelection = (id) => {
    setSelectedLeadIds(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };
  const toggleSelectAll = () => {
    const ids = filteredLeads.map(l => l.id);
    const allSelected = ids.every(id => selectedLeadIds.has(id));
    setSelectedLeadIds(allSelected ? new Set() : new Set(ids));
  };
  const clearSelection = () => setSelectedLeadIds(new Set());

  useEffect(() => { clearSelection(); }, [statusFilter, sourceFilter, searchQuery]);

  // ── Time helper ─────────────────────────────────────────────
  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '-';
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days > 0) return t('provider.leads.mgmt.timeDaysAgo', { days });
    const hrs = Math.floor(diff / 3600000);
    if (hrs > 0) return t('provider.leads.mgmt.timeHoursAgo', { hours: hrs });
    return t('provider.leads.mgmt.timeJustNow');
  };

  const getSourceBadge = (lead) => {
    const origin = resolveLeadOrigin(lead);
    if (origin === LEAD_ORIGIN.UNKNOWN) {
      // Never inferred as Marketplace: that would misstate the commission
      // channel. Shown honestly so the record can be corrected.
      return (
        <Badge variant="outline" className="text-xs text-slate-600" data-testid="lead-source-unknown-badge">
          {t('provider.leads.mgmt.sourceUnknownBadge', 'Source not recorded')}
        </Badge>
      );
    }
    if (origin === LEAD_ORIGIN.PROVIDER_OWNED) {
      return (
        <div className="flex items-center gap-1">
          <Badge className="bg-amber-100 text-amber-700 text-xs">
            <Building2 className="h-3 w-3 mr-1" />{t('provider.leads.mgmt.sourceClinicBadge')}
          </Badge>
          {lead.source_channel && (
            <Badge variant="outline" className="text-xs">{t(`provider.leads.sourceChannels.${lead.source_channel}`, SOURCE_CHANNEL_LABELS[lead.source_channel] || lead.source_channel)}</Badge>
          )}
        </div>
      );
    }
    return <Badge className="bg-purple-100 text-purple-700 text-xs"><Globe className="h-3 w-3 mr-1" />{t('provider.leads.mgmt.sourceTreatxBadge')}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="lead-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('provider.leads.mgmt.title')}</h2>
          <p className="text-gray-500">{total > 0 ? t('provider.leads.mgmt.subtitleWithTotal', { total }) : t('provider.leads.mgmt.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddLeadModal(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="add-lead-btn">
            <UserPlus className="h-4 w-4 mr-2" />{t('provider.leads.mgmt.addLeadBtn')}
          </Button>
          <Button onClick={() => setShowBulkImport(true)} variant="outline" data-testid="bulk-import-patients-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" />{t('provider.leads.bulkImport.openAction')}
          </Button>
          <Button onClick={loadLeads} variant="outline" disabled={loading} data-testid="refresh-leads-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{t('provider.leads.mgmt.refresh')}
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="lead-error">{error}</div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-sm text-gray-500">{t('provider.leads.mgmt.statTotal')}</p>
          <p className="text-2xl font-bold text-gray-900" data-testid="stat-total">{total}</p>
        </Card>
        <Card className="p-4 border-purple-200 bg-purple-50">
          <p className="text-sm text-purple-600">{t('provider.leads.mgmt.statMarketplace')}</p>
          <p className="text-2xl font-bold text-purple-700" data-testid="stat-marketplace">{marketplaceCount}</p>
        </Card>
        <Card className="p-4 border-amber-200 bg-amber-50">
          <p className="text-sm text-amber-600">{t('provider.leads.mgmt.statClinicOwned')}</p>
          <p className="text-2xl font-bold text-amber-700" data-testid="stat-provider">{providerOwnedCount}</p>
        </Card>
        <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('LOST')} data-testid="stat-terminal">
          <p className="text-sm text-gray-600">{t('provider.leads.stage.LOST')}</p>
          <p className="text-2xl font-bold text-gray-700">{lostCount}</p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={t('provider.leads.mgmt.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="lead-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="px-4 py-2 border rounded-lg" data-testid="source-filter">
            <option value="ALL">{t('provider.leads.mgmt.allSources')}</option>
            <option value="TREATX_MARKETPLACE">{t('provider.leads.mgmt.marketplaceWithCount', { count: marketplaceCount })}</option>
            <option value="PROVIDER_OWNED">{t('provider.leads.mgmt.clinicOwnedWithCount', { count: providerOwnedCount })}</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border rounded-lg" data-testid="status-filter">
            <option value="ALL">{t('provider.leads.mgmt.allStatuses')}</option>
            {['NEW', 'WORKING', 'ON_HOLD', 'LOST', 'WON'].map(stage => (
              <option key={stage} value={stage} data-testid={`stage-filter-option-${stage}`}>{t(`provider.leads.stage.${stage}`)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Bulk action toolbar */}
      {selectedLeadIds.size > 0 && (
        <div className="sticky top-0 z-10 flex items-center gap-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="bulk-action-toolbar">
          <CheckSquare className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-blue-900">{t('provider.leads.mgmt.selectedCount', { count: selectedLeadIds.size })}</span>
          <Button variant="ghost" size="sm" onClick={clearSelection} className="ml-auto"><X className="h-4 w-4 mr-1" />{t('provider.leads.mgmt.clear')}</Button>
        </div>
      )}

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          {loading && leads.length === 0 ? (
            <div className="text-center py-12 text-gray-500">{t('provider.leads.mgmt.loadingLeads')}</div>
          ) : filteredLeads.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leads-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="w-12 p-4">
                      <Checkbox checked={filteredLeads.length > 0 && filteredLeads.every(l => selectedLeadIds.has(l.id))} onCheckedChange={toggleSelectAll} data-testid="select-all" />
                    </th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('provider.leads.mgmt.colPatient')}</th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('provider.leads.mgmt.colTreatment')}</th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('provider.leads.mgmt.colBudget')}</th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('provider.leads.mgmt.colStatus')}</th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('provider.leads.mgmt.colSource')}</th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('provider.leads.mgmt.colCreated')}</th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('provider.leads.mgmt.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map(lead => {
                    const isTerminal = TERMINAL_STATUSES.includes(lead.status);
                    const isSelected = selectedLeadIds.has(lead.id);
                    return (
                      <tr key={lead.id} className={`border-b hover:bg-gray-50 ${isSelected ? 'bg-blue-50/50' : ''} ${isTerminal ? 'opacity-70' : ''}`} data-testid={`lead-row-${lead.id}`}>
                        <td className="w-12 p-4">
                          <div className="flex items-center gap-1">
                            <Checkbox checked={isSelected} onCheckedChange={() => toggleLeadSelection(lead.id)} />
                            {isTerminal && <Lock className="h-3 w-3 text-gray-400" />}
                          </div>
                        </td>
                        <td className="p-4">
                          <p className="font-medium text-gray-900">{lead.patient_name || t('provider.leads.mgmt.unknownPatient')}</p>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            {lead.patient_email && <><Mail className="h-3 w-3" />{lead.patient_email}</>}
                          </p>
                          {lead.country_of_origin && (
                            <p className="text-xs text-gray-400 flex items-center gap-1"><Globe className="h-3 w-3" />{lead.country_of_origin}</p>
                          )}
                        </td>
                        <td className="p-4"><Badge variant="outline" data-testid={`lm-treatment-${lead.id}`}>{lead.treatment_display_name || lead.treatment_interest || '-'}</Badge></td>
                        <td className="p-4 text-gray-600 text-sm">{lead.budget_range || '-'}</td>
                        <td className="p-4">
                          {/* RENEWAL-CYCLE-01 — the patient's CURRENT request cycle is the
                              actionable work; the historical offer stage stays beneath it so
                              the two never contradict each other. */}
                          {(lead.request_cycle || 1) > 1 && (
                            <div className="mb-1" data-testid={`lm-renewal-${lead.id}`}>
                              <span
                                className="inline-flex items-center rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-800"
                                title={t('provider.leads.renewal.tooltip', {
                                  cycle: lead.request_cycle,
                                  date: formatDateLocale(lead.renewed_at || lead.current_cycle_started_at),
                                })}
                              >
                                {t('provider.leads.renewal.badge', { cycle: lead.request_cycle })}
                              </span>
                            </div>
                          )}
                          <SalesStageBadge transitions={lead.status_transitions} fallbackLabel={statusLabelI18n(t, lead.status)} />
                        </td>
                        <td className="p-4">{getSourceBadge(lead)}</td>
                        <td className="p-4 text-sm text-gray-500">
                          {getTimeAgo(lead.current_cycle_started_at || lead.created_at)}
                          {(lead.request_cycle || 1) > 1 && (
                            <span className="block text-[10px] text-gray-400" data-testid={`lm-renewal-age-${lead.id}`}>
                              {t('provider.leads.renewal.originalLead', { date: formatDateLocale(lead.created_at) })}
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedLead(lead); setShowDetailModal(true); }} data-testid={`view-lead-${lead.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('provider.leads.mgmt.emptyTitle')}</h3>
              <p className="text-gray-500">{searchQuery || statusFilter !== 'ALL' ? t('provider.leads.mgmt.emptyFilter') : t('provider.leads.mgmt.emptyDefault')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lead Detail Modal */}
      {showDetailModal && selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setShowDetailModal(false)}
          onStatusChange={handleStatusChange}
          onSendInvite={handleSendInvite}
          onUploadFile={handleUploadFile}
          onListFiles={listMedicalFiles}
          onAddNote={addNote}
          onListNotes={listNotes}
          onAddMessage={addMessage}
          onListMessages={listMessages}
          onCreateOffer={createOffer}
          onListOffers={listOffers}
          onGetLatestOffer={getLatestOffer}
          onAssign={assignLead}
          onRefresh={() => { loadLeads(); }}
        />
      )}

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={showAddLeadModal}
        onClose={() => setShowAddLeadModal(false)}
        onCreated={handleCreateLead}
        clinicId={clinicId}
        salesReps={topLevelSalesReps}
      />

      {/* BULK-LEAD-IMPORT-V1 — staged import workspace */}
      <BulkLeadImport
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImported={loadLeads}
        reps={topLevelSalesReps}
      />
    </div>
  );
};

// ── Lead Detail Modal (API-driven with Notes, Messages, Offers) ─
const LeadDetailModal = ({
  lead, onClose, onStatusChange, onSendInvite, onUploadFile, onListFiles,
  onAddNote, onListNotes, onAddMessage, onListMessages,
  onCreateOffer, onListOffers, onGetLatestOffer, onAssign, onRefresh,
}) => {
  const { t } = useTranslation();
  const [medicalFiles, setMedicalFiles] = useState([]);
  const [notes, setNotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [offers, setOffers] = useState([]);
  const [clinicalReviews, setClinicalReviews] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [activeDetailTab, setActiveDetailTab] = useState('overview');

  // Form state
  const [inviting, setInviting] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [submittingMessage, setSubmittingMessage] = useState(false);
  const [showCreateOffer, setShowCreateOffer] = useState(false);
  const [creatingOffer, setCreatingOffer] = useState(false);
  const [salesReps, setSalesReps] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [assignmentRefreshKey, setAssignmentRefreshKey] = useState(0);

  // Canonical status-transition projection (from backend). If the
  // load fails we FAIL SAFE — no options are offered — because the
  // backend remains the sole authority for what is allowed.
  const [allowedTransitions, setAllowedTransitions] = useState({
    current: lead.status,
    allowed_next: null,           // null = not loaded yet
    requires_patient: [],
    patient_linked: !!lead.patient_id,
    terminal: false,
  });

  // Link Patient Account modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkEmail, setLinkEmail] = useState(lead.patient_email || '');
  const [linkSearching, setLinkSearching] = useState(false);
  const [linkResult, setLinkResult] = useState(null); // {account_id, name, email, ...} | null
  const [linkSearchReason, setLinkSearchReason] = useState('');
  const [linkConfirmMismatch, setLinkConfirmMismatch] = useState(false);
  const [linking, setLinking] = useState(false);

  const isTerminal = TERMINAL_STATUSES.includes(lead.status);
  const [markWonOpen, setMarkWonOpen] = useState(false); // LIFECYCLE-V3.2

  // Load sales reps for assignment
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    api.get(`/clinic/team`)
      .then(({ data }) => data)
      .then(data => {
        const reps = (data.members || []).filter(m => 
          m.role === 'provider_sales_rep' || m.role === 'provider_sales_rep'
        );
        setSalesReps(reps);
      })
      .catch(() => {});
  }, []);

  // Load all data for the lead
  useEffect(() => {
    (async () => {
      setLoadingData(true);
      const [filesRes, notesRes, msgsRes, offersRes] = await Promise.allSettled([
        onListFiles(lead.id),
        onListNotes(lead.id),
        onListMessages(lead.id),
        onListOffers(lead.id),
      ]);
      setMedicalFiles(filesRes.status === 'fulfilled' ? filesRes.value.files || [] : []);
      setNotes(notesRes.status === 'fulfilled' ? notesRes.value.notes || [] : []);
      setMessages(msgsRes.status === 'fulfilled' ? msgsRes.value.messages || [] : []);
      setOffers(offersRes.status === 'fulfilled' ? offersRes.value.offers || [] : []);
      setLoadingData(false);
    })();
  }, [lead.id, onListFiles, onListNotes, onListMessages, onListOffers]);


  // Fetch the canonical allowed-transitions projection when the modal
  // opens for a given lead. FAIL-SAFE on error: `allowed_next` remains
  // null which the dropdown treats as "no options to offer" — better
  // than presenting invalid transitions.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/clinic-leads/${lead.id}/status-transitions`);
        if (!cancelled) setAllowedTransitions(data);
      } catch (err) {
        if (!cancelled) setAllowedTransitions((prev) => ({ ...prev, allowed_next: [] }));
      }
    })();
    return () => { cancelled = true; };
  }, [lead.id, lead.status, lead.patient_id]);

  const handleInvite = async () => {
    setInviting(true);
    try { await onSendInvite(lead.id); onRefresh(); } catch {}
    setInviting(false);
  };

  // ── Link Patient Account (canonical manual link) ────────────
  const openLinkModal = () => {
    setLinkEmail(lead.patient_email || '');
    setLinkResult(null);
    setLinkSearchReason('');
    setLinkConfirmMismatch(false);
    setShowLinkModal(true);
  };
  const closeLinkModal = () => {
    setShowLinkModal(false);
    setLinking(false);
  };
  const searchPatient = async () => {
    const email = (linkEmail || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast({ title: t('provider.leads.link.invalidEmail', 'Please enter a valid email'), variant: 'destructive' });
      return;
    }
    setLinkSearching(true);
    setLinkResult(null);
    setLinkSearchReason('');
    try {
      const { data } = await api.get(`/clinic-leads/patient-lookup`, { params: { email } });
      if (data?.result) {
        setLinkResult(data.result);
      } else {
        setLinkSearchReason(data?.reason || 'no_registered_patient');
      }
    } catch (err) {
      toast({ title: t('provider.leads.link.searchFailed', 'Patient lookup failed'), description: err?.response?.data?.detail || err?.message, variant: 'destructive' });
    } finally {
      setLinkSearching(false);
    }
  };
  const confirmLink = async () => {
    if (!linkResult) return;
    const emailMismatch = (linkResult.email || '').toLowerCase() !== (lead.patient_email || '').toLowerCase();
    if (emailMismatch && !linkConfirmMismatch) {
      toast({ title: t('provider.leads.link.needConfirm', 'Please confirm the email mismatch'), variant: 'destructive' });
      return;
    }
    setLinking(true);
    try {
      const { data } = await api.post(`/clinic-leads/${lead.id}/link-patient-account`, {
        account_id: linkResult.account_id,
        confirm_email_mismatch: emailMismatch,
      });
      if (data?.success) {
        toast({ title: t('provider.leads.link.success', 'Patient account linked') });
        setShowLinkModal(false);
        onRefresh();
        onClose();
      }
    } catch (err) {
      toast({ title: t('provider.leads.link.failed', 'Link failed'), description: err?.response?.data?.detail || err?.message, variant: 'destructive' });
    } finally {
      setLinking(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onUploadFile(lead.id, file, 'general');
    try { const d = await onListFiles(lead.id); setMedicalFiles(d.files || []); } catch {}
    e.target.value = '';
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSubmittingNote(true);
    try {
      await onAddNote(lead.id, newNote.trim());
      setNewNote('');
      const d = await onListNotes(lead.id);
      setNotes(d.notes || []);
    } catch {}
    setSubmittingNote(false);
  };

  const handleAddMessage = async () => {
    if (!newMessage.trim()) return;
    setSubmittingMessage(true);
    try {
      await onAddMessage(lead.id, newMessage.trim(), 'OUTBOUND');
      setNewMessage('');
      const d = await onListMessages(lead.id);
      setMessages(d.messages || []);
    } catch {}
    setSubmittingMessage(false);
  };

  // P0-3 FIX: Offer creation from admin panel
  const handleCreateOffer = async (offerData) => {
    setCreatingOffer(true);
    try {
      const backendPayload = {
        treatment_name: offerData.treatment || lead?.treatment_interest || '',
        base_price: parseFloat(offerData.basePrice) || 0,
        currency: offerData.currency,  // PHASE 2B/B1 — backend policy validates; no client default
        // TREATX-TAX-5 — provider-declared treatment VAT (null = not declared).
        provider_tax: buildProviderTaxPayload(offerData),
        included_items: (offerData.includedItems || []).map(item => ({
          name: item.name || '',
          description: item.description || '',
          price: parseFloat(item.price) || 0,
          included: item.included !== false,
        })),
        notes_patient_visible: offerData.notes || '',
        notes_internal: '',
        // TREATX-BATCH-1.1 — OfferComposer's `validUntil` stores validity
        // in DAYS (free text integer), NOT an ISO date. The prior
        // `new Date("30")` derivation produced NaN → JSON null → backend
        // 422, blocking every admin-side offer/revision creation. Match
        // the SalesRepDashboard mapping (parseInt).
        valid_days: (() => {
          const _v = parseInt(offerData.validUntil, 10);
          return Number.isFinite(_v) && _v > 0 ? _v : 30;
        })(),
        hotel_nights: offerData.hotelNights ? parseInt(offerData.hotelNights, 10) : null,
        hotel_type: offerData.hotelType || null,
        airport_transfer: offerData.airportTransfer ?? null,
        proposed_dates: offerData.proposedDates || null,
        deposit_type: offerData.depositType || 'percentage',
        deposit_value: Number.isFinite(Number(offerData.depositValue)) && offerData.depositValue !== '' ? Number(offerData.depositValue) : null,
        payment_structure: offerData.paymentStructure || 'single',
        installment_count: offerData.paymentStructure === 'installments' ? Number(offerData.installmentCount) : null,
        additional_services: (offerData.additionalServices || []).map(s => ({
          name: s.name || '',
          price: parseFloat(s.price) || 0,
          quantity: parseInt(s.quantity, 10) || 1,
        })),
        terms: offerData.terms || '',
        // TREATX-BATCH-1.1 — Revision lineage forwarded so the backend
        // stamps canonical parent_offer_id + source_offer_id.
        parent_offer_id: offerData.parentOfferId || null,
        // MULTIPLE-ACTIONABLE-ALTERNATIVES — intent from the composer. revise (with a
        // target) replaces only that offer; alternative adds a co-valid option.
        offer_mode: offerData.offer_mode || 'alternative',
        supersedes_quote_id: offerData.supersedes_quote_id || null,
      };
      await onCreateOffer(lead.id, backendPayload);
      setShowCreateOffer(false);
      // Refresh offers list
      try { const d = await onListOffers(lead.id); setOffers(d.offers || []); } catch {}
      onRefresh();
      toast({ title: t('provider.leads.mgmt.toastOfferSentTitle'), description: t('provider.leads.mgmt.toastOfferSentDesc', { name: lead.patient_name || t('provider.leads.mgmt.fallbackPatient') }) });
    } catch (err) {
      console.error('Create offer failed:', err);
      toast({ title: t('provider.leads.mgmt.toastOfferFailedTitle'), description: formatOfferError(err, t, t('provider.leads.mgmt.toastOfferFailedDefault')), variant: 'destructive' });
      throw err;
    } finally {
      setCreatingOffer(false);
    }
  };

  const tabs = [
    { id: 'overview', label: t('provider.leads.detail.tabOverview') },
    { id: 'notes', label: t('provider.leads.detail.tabNotes', { count: notes.length }) },
    { id: 'messages', label: t('provider.leads.detail.tabMessages', { count: messages.length }) },
    { id: 'offers', label: t('provider.leads.detail.tabOffers', { count: offers.length }) },
    { id: 'clinical-reviews', label: t('provider.leads.detail.tabClinicalReviews', { count: clinicalReviews.length }) },
    { id: 'files', label: t('provider.leads.detail.tabFiles', { count: medicalFiles.length }) },
  ];

  // M16B-06A — reload clinical reviews for this lead.
  const reloadClinicalReviews = useCallback(async () => {
    try {
      const { data } = await api.get(`/clinic/leads/${lead.id}/clinical-reviews`);
      setClinicalReviews(data?.reviews || []);
    } catch (err) {
      // 404 (cross-tenant) silently produces empty list
      setClinicalReviews([]);
    }
  }, [lead.id]);

  useEffect(() => { reloadClinicalReviews(); }, [reloadClinicalReviews]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="lead-detail-modal">
      <Card className="w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10 border-b">
          <div>
            <CardTitle className="flex items-center gap-2">
              {lead.patient_name || t('provider.leads.mgmt.unknownPatient')}
              {lead.patient_id && <Badge className="bg-emerald-100 text-emerald-700 text-xs"><UserCheck className="h-3 w-3 mr-1" />{t('provider.leads.detail.linkedBadge')}</Badge>}
              <SalesStageBadge transitions={allowedTransitions.human_stage ? allowedTransitions : lead.status_transitions} fallbackLabel={statusLabelI18n(t, lead.status)} />
            </CardTitle>
            <CardDescription data-testid="lm-detail-treatment">{lead.treatment_display_name || lead.treatment_interest} {lead.budget_range && `- ${lead.budget_range}`}</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-detail-modal"><X className="h-5 w-5" /></Button>
        </CardHeader>

        {/* Tabs */}
        <div className="flex border-b px-6 gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveDetailTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeDetailTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <CardContent className="p-6">
          {loadingData ? (
            <div className="text-center py-8 text-gray-500">{t('provider.leads.detail.loading')}</div>
          ) : (
            <>
              {/* ─── Overview Tab ─── */}
              {activeDetailTab === 'overview' && (
                <div className="space-y-6">
                  {/* M16B-07B — Treatment Ready (per slug, clinic-evidence) */}
                  <TreatmentReadyPanel
                    leadId={lead.id}
                    mode="clinic"
                    title={t('provider.leads.detail.treatmentReadiness')}
                  />

                  {/* Contact */}
                  <div className="grid grid-cols-2 gap-3 bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-gray-400" />{lead.patient_email || '-'}</div>
                    <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-gray-400" />{lead.patient_phone || '-'}</div>
                    <div className="flex items-center gap-2 text-sm"><Globe className="h-4 w-4 text-gray-400" />{lead.country_of_origin || '-'}</div>
                    <div className="flex items-center gap-2 text-sm"><Calendar className="h-4 w-4 text-gray-400" />{lead.created_at ? formatDateLocale(lead.created_at) : '-'}</div>
                  </div>

                  {/* Source. REVIEW FIX — this badge said "zero commission"
                      while the canonical CommissionEngine never resolves a
                      provider_owned lane to zero (global default 5%, package
                      overrides 5/4/3%, floor 3% — `commission_engine.
                      PROVIDER_OWNED_MIN_COMMISSION_BPS`), and the proven
                      payment froze provider_owned at 400 bps. The rate itself
                      is NOT restated here: it depends on the clinic's
                      entitlement package and, once accepted, on the contract's
                      immutable snapshot, both of which the offer and contract
                      surfaces already show from the authority. */}
                  <div className={`p-4 rounded-lg border ${resolveLeadOrigin(lead) === LEAD_ORIGIN.PROVIDER_OWNED ? 'bg-amber-50 border-amber-200' : resolveLeadOrigin(lead) === LEAD_ORIGIN.MARKETPLACE ? 'bg-purple-50 border-purple-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center gap-2">
                      {resolveLeadOrigin(lead) === LEAD_ORIGIN.PROVIDER_OWNED
                        ? <><Building2 className="h-4 w-4 text-amber-600" /><span className="font-medium text-amber-800" data-testid="lead-source-provider-owned">{t('provider.leads.detail.clinicOwnedSource')}</span></>
                        : resolveLeadOrigin(lead) === LEAD_ORIGIN.MARKETPLACE
                          ? <><Globe className="h-4 w-4 text-purple-600" /><span className="font-medium text-purple-800" data-testid="lead-source-marketplace">{t('provider.leads.detail.treatxMarketplace')}</span></>
                          : <span className="font-medium text-slate-700" data-testid="lead-source-unknown">{t('provider.leads.detail.sourceUnknown', 'Lead source not recorded')}</span>}
                    </div>
                    {resolveLeadOrigin(lead) === LEAD_ORIGIN.PROVIDER_OWNED && (
                      <p className="text-sm text-amber-700 mt-1" data-testid="lead-source-commission-note">
                        {t('provider.leads.detail.clinicOwnedCommissionNote')}
                      </p>
                    )}
                    {lead.source_channel && <p className="text-sm text-gray-600 mt-1">{t('provider.leads.detail.channelLabel', { channel: t(`provider.leads.sourceChannels.${lead.source_channel}`, SOURCE_CHANNEL_LABELS[lead.source_channel] || lead.source_channel) })}</p>}
                    {lead.assigned_agent_name && <p className="text-sm text-gray-600 mt-1">{t('provider.leads.detail.assignedTo', { name: lead.assigned_agent_name })}</p>}
                  </div>

                  {/* Lead Assignment */}
                  {!isTerminal && salesReps.length > 0 && (
                    <div className="flex items-center gap-2" data-testid="lead-assign-section">
                      <span className="text-sm font-medium text-gray-700">{t('provider.leads.detail.assignTo')}</span>
                      <select
                        key={`assign-${lead.id}-${lead.assigned_agent_id || 'none'}`}
                        defaultValue={lead.assigned_agent_id || ''}
                        onChange={async (e) => {
                          const rep = salesReps.find(r => r.user_id === e.target.value);
                          if (!rep) return;
                          setAssigning(true);
                          try {
                            await onAssign(lead.id || lead.lead_id, rep.user_id, rep.name);
                            onRefresh();
                            // Force AssignmentPanel to re-fetch its canonical
                            // owner+history projection so the card matches
                            // the dropdown selection immediately.
                            setAssignmentRefreshKey(k => k + 1);
                          } catch (err) {
                            // B-3 — surface assign failures to the admin.
                            // Prior behaviour swallowed 403/409 into
                            // console.error, leaving the <select> label
                            // switched to a rep that was never persisted.
                            const msg =
                              err?.response?.data?.detail
                              || err?.response?.data?.message
                              || err?.message
                              || t('provider.leads.detail.assignFailed', 'Assignment failed');
                            toast.error(msg);
                            // Revert the picker to the canonical current owner.
                            e.target.value = lead.assigned_agent_id || '';
                            console.error('Assign failed:', err);
                          }
                          setAssigning(false);
                        }}
                        className="flex-1 p-2 border rounded-md text-sm"
                        data-testid="assign-rep-select"
                        disabled={assigning}
                      >
                        <option value="">{t('provider.leads.detail.selectRep')}</option>
                        {salesReps
                          .filter(rep => rep.user_id !== lead.assigned_agent_id)
                          .map(rep => (
                          <option key={rep.user_id} value={rep.user_id}>{rep.name} ({rep.email})</option>
                        ))}
                      </select>
                      {assigning && <span className="text-xs text-gray-400">{t('provider.leads.detail.assigning')}</span>}
                    </div>
                  )}

                  {/* Assignment History Panel */}
                  <AssignmentPanel
                    entityType="lead"
                    entityId={lead.id}
                    clinicId={lead.clinic_id}
                    userRole={localStorage.getItem('treatx_role') || ''}
                    refreshKey={assignmentRefreshKey}
                    onAssignmentChange={() => { onRefresh(); setAssignmentRefreshKey(k => k + 1); }}
                  />

                  {/* Status */}
                  <MarkAsWonDialog lead={lead} open={markWonOpen} onClose={() => setMarkWonOpen(false)} onDone={() => { onRefresh && onRefresh(); onClose && onClose(); }} />
                  {/* SALES-CRM-SIMPLIFICATION — explicit business actions instead of an internal-stage dropdown */}
                  <div className="space-y-2" data-testid="status-transition-panel">
                    <SalesActions
                      lead={lead}
                      transitions={allowedTransitions.human_stage ? allowedTransitions : null}
                      onStatus={async (status, reason) => { await onStatusChange(lead.id, status, reason || undefined); onRefresh(); onClose(); }}
                      onMarkWon={() => setMarkWonOpen(true)}
                      onDone={() => { onRefresh && onRefresh(); onClose && onClose(); }}
                    />
                    {allowedTransitions.human_stage === 'WON' && (
                      <p className="text-xs text-slate-500" data-testid="status-terminal-hint">{t('provider.leads.actions.wonHint')}</p>
                    )}
                  </div>

                  {/* Patient Linkage — OPTIONAL, not blocking */}
                  {resolveLeadOrigin(lead) === LEAD_ORIGIN.PROVIDER_OWNED && !lead.patient_id && lead.patient_email && (
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200" data-testid="patient-linkage-panel">
                      <p className="text-sm text-slate-600 mb-1">{t('provider.leads.detail.patientNotLinked')}</p>
                      <p className="text-xs text-slate-500 mb-3">{t('provider.leads.detail.patientNotLinkedHelp')}</p>
                      <div className="flex flex-wrap gap-2">
                        <Button onClick={openLinkModal} size="sm" variant="default" data-testid="link-patient-account-btn">
                          <UserCheck className="h-4 w-4 mr-2" />{t('provider.leads.link.button', 'Link Patient Account')}
                        </Button>
                        <Button onClick={handleInvite} disabled={inviting} size="sm" variant="outline" data-testid="send-invite-btn">
                          <Send className="h-4 w-4 mr-2" />{inviting ? t('provider.leads.detail.sending') : t('provider.leads.detail.sendInviteOptional')}
                        </Button>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-2">
                        {t('provider.leads.link.helper', 'Use "Link Patient Account" if the patient already has a TreatX account. Use "Send Invitation" if the patient has not registered yet.')}
                      </p>
                    </div>
                  )}
                  {lead.patient_id && (
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <UserCheck className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm text-emerald-800 font-medium">{t('provider.leads.detail.patientLinked', { id: lead.patient_id.slice(0, 8) })}</span>
                    </div>
                  )}
                </div>
              )}

              {/* ─── Notes Tab ─── */}
              {activeDetailTab === 'notes' && (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder={isTerminal ? t('provider.leads.detail.noteAddAudit') : t('provider.leads.detail.noteAddInternal')}
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="flex-1"
                      data-testid="note-input"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                    />
                    <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim() || submittingNote} data-testid="add-note-btn">
                      {submittingNote ? '...' : t('provider.leads.detail.addBtn')}
                    </Button>
                  </div>
                  {notes.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {notes.map(n => (
                        <div key={n.id} className={`p-3 rounded-lg text-sm ${n.note_type === 'AUDIT' ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium text-gray-700">{n.user_name} <span className="text-gray-400 text-xs">({n.user_role})</span></span>
                            <span className="text-xs text-gray-400">{formatDateTimeLocale(n.created_at)}</span>
                          </div>
                          <p className="text-gray-800">{n.content}</p>
                          {n.note_type === 'AUDIT' && <Badge className="bg-red-100 text-red-700 text-xs mt-1">{t('provider.leads.detail.auditNoteBadge')}</Badge>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">{t('provider.leads.detail.noNotes')}</p>
                  )}
                </div>
              )}

              {/* ─── Messages Tab ─── */}
              {activeDetailTab === 'messages' && (
                <div className="space-y-4">
                  {!isTerminal ? (
                    <div className="flex gap-2">
                      <Input
                        placeholder={t('provider.leads.detail.logCommunicationPh')}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="flex-1"
                        data-testid="message-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddMessage()}
                      />
                      <Button size="sm" onClick={handleAddMessage} disabled={!newMessage.trim() || submittingMessage} data-testid="add-message-btn">
                        {submittingMessage ? '...' : t('provider.leads.detail.sendBtn')}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">{t('provider.leads.detail.messagesLocked', { status: statusLabelI18n(t, lead.status) })}</p>
                  )}
                  {messages.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {messages.map(m => (
                        <div key={m.id} className={`p-3 rounded-lg text-sm ${m.direction === 'INBOUND' ? 'bg-blue-50' : 'bg-gray-50'}`}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium text-gray-700">
                              {m.direction === 'INBOUND' ? t('provider.leads.detail.inboundLabel') : m.user_name}
                              <span className="text-gray-400 text-xs ml-1">[{m.direction}]</span>
                            </span>
                            <span className="text-xs text-gray-400">{formatDateTimeLocale(m.created_at)}</span>
                          </div>
                          <p className="text-gray-800">{m.content}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">{t('provider.leads.detail.noMessages')}</p>
                  )}
                </div>
              )}

              {/* ─── Offers Tab ─── */}
              {activeDetailTab === 'offers' && (
                <div className="space-y-4">
                  {/* P0-3 FIX: Create Offer button — opens OfferComposer */}
                  {!isTerminal && !showCreateOffer && (
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => setShowCreateOffer(true)}
                      disabled={creatingOffer}
                      data-testid="create-offer-btn"
                    >
                      <FileText className="h-4 w-4 mr-1" />{t('provider.leads.detail.createOffer')}
                    </Button>
                  )}
                  {showCreateOffer && (
                    <OfferComposer
                      lead={lead}
                      existingOffer={offers.length > 0 ? offers[0] : null}
                      onOfferSent={handleCreateOffer}
                      onCancel={() => setShowCreateOffer(false)}
                    />
                  )}
                  {offers.length > 0 ? (
                    <div className="space-y-3">
                      {offers.map(o => (
                        <div key={o.id} className={`p-4 border rounded-lg ${o.is_latest ? 'border-blue-300 bg-blue-50' : 'bg-gray-50'}`}>
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">v{o.version_number}</span>
                              <Badge variant="outline">{o.status}</Badge>
                              {o.is_latest && <Badge className="bg-blue-100 text-blue-700 text-xs">{t('provider.leads.detail.offerLatest')}</Badge>}
                            </div>
                            <span className="font-bold text-lg" data-testid={`clinic-offer-total-${o.id}`}>
                              {/* TREATX-BATCH-1-CANONICAL-SSOT — render
                                  canonical Total from `financial_summary`,
                                  not raw base_price. Same rule as patient
                                  surfaces (single financial truth). */}
                              {(() => {
                                const fs = o.financial_summary || {};
                                const total = Number.isFinite(fs.total) ? fs.total : null;
                                const cur = fs.currency || o.currency || '';
                                return total != null ? `${total.toLocaleString()} ${cur}` : `${o.base_price ?? '—'} ${o.currency ?? ''}`;
                              })()}
                            </span>
                          </div>
                          {o.included_items && o.included_items.length > 0 && (
                            <div className="text-sm text-gray-600 mb-1">
                              {o.included_items.map(i => i.name).join(', ')}
                            </div>
                          )}
                          <div className="text-xs text-gray-400">
                            {t('provider.leads.detail.offerCreatedOn', { date: formatDateLocale(o.created_at) })}
                            {o.valid_until && ` | ${t('provider.leads.detail.offerValidUntil', { date: formatDateLocale(o.valid_until) })}`}
                          </div>
                          {o.notes_patient_visible && <p className="text-sm text-gray-600 mt-2 italic">{o.notes_patient_visible}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">{t('provider.leads.detail.noOffers')}</p>
                  )}
                </div>
              )}

              {/* ─── Clinical Reviews Tab (M16B-06A) ─── */}
              {activeDetailTab === 'clinical-reviews' && (
                <ClinicalReviewsPanel
                  lead={lead}
                  reviews={clinicalReviews}
                  onReload={reloadClinicalReviews}
                  data-testid="clinical-reviews-panel"
                />
              )}

              {/* ─── Files Tab ─── */}
              {activeDetailTab === 'files' && (
                <div className="space-y-4">
                  {medicalFiles.length > 0 && (
                    <div className="space-y-2">
                      {medicalFiles.map(f => (
                        <div key={f.id} className="flex items-center justify-between p-3 bg-gray-50 rounded text-sm">
                          <div>
                            <p className="font-medium">{f.original_filename}</p>
                            <p className="text-xs text-gray-400">{f.category} - {formatDateLocale(f.created_at)}</p>
                          </div>
                          <span className="text-gray-400">{(f.file_size / 1024).toFixed(1)} KB</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm text-gray-600" data-testid="upload-medical-file">
                    <Upload className="h-4 w-4" />{t('provider.leads.detail.uploadMedicalFile')}
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFileSelect} />
                  </label>
                </div>
              )}
            </>
          )}

          {/* Footer */}
          <div className="pt-4 mt-4 border-t flex justify-end">
            <Button variant="outline" onClick={onClose}><ChevronLeft className="h-4 w-4 mr-1" />{t('provider.leads.detail.back')}</Button>
          </div>
        </CardContent>
      </Card>

      {/* ─── Link Patient Account Modal (canonical) ─── */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="link-patient-modal">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <UserCheck className="h-4 w-4 text-emerald-600" />
                {t('provider.leads.link.title', 'Link Patient Account')}
              </CardTitle>
              <CardDescription className="text-xs">
                {t('provider.leads.link.desc', 'Search by exact email for an existing registered patient account, then confirm the link.')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder={t('provider.leads.link.emailPlaceholder', 'patient@email.com')}
                  value={linkEmail}
                  onChange={(e) => setLinkEmail(e.target.value)}
                  className="flex-1"
                  data-testid="link-email-input"
                  onKeyDown={(e) => e.key === 'Enter' && searchPatient()}
                />
                <Button size="sm" onClick={searchPatient} disabled={linkSearching} data-testid="link-search-btn">
                  <Search className="h-4 w-4 mr-1" />
                  {linkSearching ? t('provider.leads.link.searching', 'Searching…') : t('provider.leads.link.search', 'Search')}
                </Button>
              </div>

              {linkSearchReason === 'no_registered_patient' && (
                <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2" data-testid="link-no-result">
                  {t('provider.leads.link.noResult', 'No registered patient account was found for this email. Use "Send Invitation" instead.')}
                </div>
              )}
              {linkSearchReason === 'not_a_patient_account' && (
                <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2" data-testid="link-not-patient">
                  {t('provider.leads.link.notPatient', 'That account exists but is not a registered patient. Cannot link.')}
                </div>
              )}

              {linkResult && (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg border bg-emerald-50 border-emerald-200" data-testid="link-search-result">
                    <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                      <UserCheck className="h-4 w-4" /> {linkResult.name || t('provider.leads.link.unknownName', '(no name)')}
                    </div>
                    <div className="text-xs text-slate-700 mt-1">{linkResult.email}</div>
                    {linkResult.country && (
                      <div className="text-xs text-slate-500 mt-0.5">{linkResult.country}</div>
                    )}
                  </div>

                  {(linkResult.email || '').toLowerCase() !== (lead.patient_email || '').toLowerCase() && (
                    <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 space-y-2" data-testid="link-mismatch-warn">
                      <div className="flex items-start gap-2 text-amber-800">
                        <span className="font-medium">
                          {t('provider.leads.link.mismatchTitle', 'Email mismatch')}
                        </span>
                      </div>
                      <div className="text-amber-700">
                        {t(
                          'provider.leads.link.mismatchBody',
                          'The lead email ({{lead}}) differs from the account email ({{acct}}). This override will be audit-logged.',
                          { lead: lead.patient_email, acct: linkResult.email }
                        )}
                      </div>
                      <label className="flex items-center gap-2 text-amber-800 cursor-pointer">
                        <Checkbox
                          checked={linkConfirmMismatch}
                          onCheckedChange={(v) => setLinkConfirmMismatch(!!v)}
                          data-testid="link-confirm-mismatch"
                        />
                        <span>{t('provider.leads.link.mismatchConfirm', 'I confirm this is the correct patient.')}</span>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
            <div className="p-4 pt-0 flex justify-end gap-2 border-t mt-2 pt-3">
              <Button variant="outline" size="sm" onClick={closeLinkModal} disabled={linking} data-testid="link-cancel-btn">
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                size="sm"
                onClick={confirmLink}
                disabled={!linkResult || linking}
                data-testid="link-confirm-btn"
              >
                {linking ? t('provider.leads.link.linking', 'Linking…') : t('provider.leads.link.confirm', 'Link Account')}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ─── M16B-06A Clinical Reviews Panel (request + list) ───
const ClinicalReviewsPanel = ({ lead, reviews, onReload }) => {
  const { t } = useTranslation();
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/clinic/team`).then(({ data }) => {
      const docs = (data?.members || []).filter(m => m.role === 'provider_doctor' || (m.role_assignments || []).some(r => r.role_id === 'provider_doctor'));
      setDoctors(docs);
      if (docs[0]) setSelectedDoctor(docs[0].user_id || docs[0].id);
    }).catch(() => setDoctors([]));
  }, []);

  const handleRequest = async () => {
    if (!selectedDoctor) {
      toast({ title: t('provider.leads.clinicalReview.toastNeedDoctor'), variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/clinic/leads/${lead.id}/clinical-reviews`, {
        doctor_user_id: selectedDoctor,
        reason: reason.trim() || undefined,
      });
      setReason('');
      setShowRequestForm(false);
      await onReload();
      toast({ title: t('provider.leads.clinicalReview.toastReviewRequested') });
    } catch (err) {
      toast({ title: t('provider.leads.clinicalReview.toastRequestFailed'), description: err?.response?.data?.detail?.message || err?.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = {
    requested:  'bg-amber-100 text-amber-800',
    submitted:  'bg-emerald-100 text-emerald-800',
    superseded: 'bg-slate-200 text-slate-700',
    cancelled:  'bg-rose-100 text-rose-700',
  };
  const recLabelI18n = (r) => ({
    suitable: t('provider.leads.clinicalReview.recSuitable'),
    suitable_with_conditions: t('provider.leads.clinicalReview.recSuitableCond'),
    needs_more_information: t('provider.leads.clinicalReview.recNeedsMore'),
    not_suitable: t('provider.leads.clinicalReview.recNotSuitable'),
  })[r] || r;

  return (
    <div className="space-y-4" data-testid="clinical-reviews-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">{t('provider.leads.clinicalReview.clinicalReviewsHeading')}</h3>
          <p className="text-xs text-gray-500">{t('provider.leads.clinicalReview.clinicalReviewsHelp')}</p>
        </div>
        <Button size="sm" onClick={() => setShowRequestForm(s => !s)} data-testid="request-clinical-review-toggle">
          {showRequestForm ? t('provider.leads.clinicalReview.cancel') : t('provider.leads.clinicalReview.requestReviewBtn')}
        </Button>
      </div>

      {showRequestForm && (
        <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg space-y-3" data-testid="request-clinical-review-form">
          <div>
            <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.doctorLabel')}</label>
            <select
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm"
              value={selectedDoctor}
              onChange={e => setSelectedDoctor(e.target.value)}
              data-testid="clinical-review-doctor-select"
            >
              {doctors.length === 0 && <option value="">{t('provider.leads.clinicalReview.noDoctorsOption')}</option>}
              {doctors.map(d => (
                <option key={d.user_id || d.id} value={d.user_id || d.id}>
                  {d.name || d.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.reasonLabel')}</label>
            <textarea
              rows={3}
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm"
              placeholder={t('provider.leads.clinicalReview.reasonPh')}
              value={reason}
              onChange={e => setReason(e.target.value)}
              data-testid="clinical-review-reason"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowRequestForm(false)}>{t('provider.leads.clinicalReview.cancel')}</Button>
            <Button size="sm" disabled={submitting || !selectedDoctor} onClick={handleRequest} data-testid="clinical-review-submit-request">
              {submitting ? t('provider.leads.clinicalReview.requesting') : t('provider.leads.clinicalReview.submitRequest')}
            </Button>
          </div>
        </div>
      )}

      {reviews.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4" data-testid="clinical-reviews-empty">{t('provider.leads.clinicalReview.noReviews')}</p>
      ) : (
        <div className="space-y-3">
          {reviews.map(r => (
            <div
              key={r.clinical_review_id}
              className="border rounded-lg p-3 text-sm bg-white"
              data-testid={`clinical-review-${r.clinical_review_id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium text-gray-900">
                  {t('provider.leads.clinicalReview.reviewLabel', { no: r.review_no })} <span className="text-xs text-gray-500 font-normal">· {r.doctor_name}{r.doctor_specialty ? ` · ${r.doctor_specialty}` : ''}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${statusColor[r.status] || 'bg-gray-100 text-gray-700'}`} data-testid={`clinical-review-status-${r.clinical_review_id}`}>
                  {r.status}
                </span>
              </div>
              {r.recommendation && (
                <div className="text-xs text-gray-700 mb-1">
                  <span className="font-medium">{t('provider.leads.clinicalReview.recommendationLabel')}</span>{recLabelI18n(r.recommendation)}
                </div>
              )}
              {(r.recommended_treatments || []).length > 0 && (
                <div className="text-xs text-gray-700">
                  <span className="font-medium">{t('provider.leads.clinicalReview.recommendedLabel')}</span>
                  {(r.recommended_treatments || []).map(t => t.label || t.slug).join(', ')}
                </div>
              )}
              {(r.additional_treatments || []).length > 0 && (
                <div className="text-xs text-gray-700">
                  <span className="font-medium">{t('provider.leads.clinicalReview.additionalLabel')}</span>
                  {(r.additional_treatments || []).map(t => t.label || t.slug).join(', ')}
                </div>
              )}
              {(r.alternative_treatments || []).length > 0 && (
                <div className="text-xs text-gray-700">
                  <span className="font-medium">{t('provider.leads.clinicalReview.alternativeLabel')}</span>
                  {(r.alternative_treatments || []).map(t => t.label || t.slug).join(', ')}
                </div>
              )}
              {(r.removed_or_rejected_treatments || []).length > 0 && (
                <div className="text-xs text-rose-700">
                  <span className="font-medium">{t('provider.leads.clinicalReview.removedLabel')}</span>
                  {(r.removed_or_rejected_treatments || []).map(t => t.label || t.slug).join(', ')}
                </div>
              )}
              {r.patient_visible_summary && (
                <div className="mt-2 p-2 bg-emerald-50 rounded text-xs text-emerald-900">
                  <span className="font-medium">{t('provider.leads.clinicalReview.patientSummary')}</span>{r.patient_visible_summary}
                </div>
              )}
              {r.sales_rep_guidance && (
                <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-900">
                  <span className="font-medium">{t('provider.leads.clinicalReview.salesGuidance')}</span>{r.sales_rep_guidance}
                </div>
              )}
              {r.doctor_notes && (
                <details className="mt-2 text-xs text-gray-700">
                  <summary className="cursor-pointer font-medium">{t('provider.leads.clinicalReview.internalDoctorNotes')}</summary>
                  <p className="mt-1 whitespace-pre-wrap text-gray-600">{r.doctor_notes}</p>
                </details>
              )}
              <div className="mt-2 flex flex-wrap gap-3 text-[10px] uppercase tracking-wide text-gray-400">
                <span>{r.created_at ? t('provider.leads.clinicalReview.metaCreated', { date: formatDateTimeLocale(r.created_at) }) : '-'}</span>
                {r.submitted_at && <span>{t('provider.leads.clinicalReview.metaSubmitted', { date: formatDateTimeLocale(r.submitted_at) })}</span>}
                {(r.required_follow_up || []).length > 0 && <span>{t('provider.leads.clinicalReview.metaFollowUp', { items: (r.required_follow_up || []).join(', ') })}</span>}
              </div>
              {/* M16B-06C — sales rep CTA to seed a revised offer draft */}
              {r.status === 'submitted' && (
                <BuildRevisedOfferButton review={r} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── M16B-06D — sales rep CTA: opens a full editable revised-offer form
// seeded from the doctor's review and submits via the canonical write
// endpoint. The form is the production workflow surface — no JSON
// editing, no raw payload paste.
const BuildRevisedOfferButton = ({ review }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border-t pt-2" data-testid={`build-revised-offer-${review.clinical_review_id}`}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`build-revised-offer-btn-${review.clinical_review_id}`}>
        {t('provider.leads.clinicalReview.buildRevisedOfferBtn')}
      </Button>
      {open && (
        <RevisedOfferEditor
          review={review}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};

// ─── M16B-06D — Revised Offer Editor (modal) ───
const RevisedOfferEditor = ({ review, onClose }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [createdOffer, setCreatedOffer] = useState(null);

  // Editable state
  const [treatmentSlug, setTreatmentSlug] = useState('');
  const [treatmentName, setTreatmentName] = useState('');
  const [basePrice, setBasePrice]       = useState('');
  const [currency, setCurrency]         = useState('');
  // PHASE 2B/B1 — backend-authoritative allowed currencies (null = loading).
  const [currencyPolicy, setCurrencyPolicy] = useState(null);
  const [validDays, setValidDays]       = useState(14);
  const [notesPatient, setNotesPatient] = useState('');
  const [notesInternal, setNotesInternal] = useState('');
  const [hotelNights, setHotelNights]   = useState('');
  const [hotelType, setHotelType]       = useState('');
  const [airportTransfer, setAirportTransfer] = useState(false);
  const [items, setItems]               = useState([]); // {item_type, description, price, currency, treatment_slug}
  const [parentOfferId, setParentOfferId] = useState('');
  const [existingOffers, setExistingOffers] = useState([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [draftRes, catRes, lineageRes, policyRes] = await Promise.all([
          api.post(`/clinic/clinical-reviews/${review.clinical_review_id}/draft-revised-offer`),
          api.get('/public/catalog/treatments'),
          api.get(`/clinic/leads/${review.lead_id}/offer-lineage`),
          api.get(`/clinic-leads/${review.lead_id}/currency-policy`).catch(() => ({ data: { resolved: false, reason: 'policy_fetch_failed' } })),
        ]);
        if (cancel) return;
        const pol = policyRes.data || { resolved: false };
        setCurrencyPolicy(pol);
        const _allowed = pol.resolved ? (pol.allowed_currencies || []) : [];
        const d = draftRes.data?.draft || {};
        setCurrency(_allowed.includes(d.currency) ? d.currency : (_allowed[0] || ''));
        setDraft(d);
        setCatalog(catRes.data?.treatments || []);
        setExistingOffers(lineageRes.data?.lineage?.offers || []);
        // Seed editable state from server draft
        setTreatmentSlug(d.treatment_slug || '');
        setTreatmentName(d.treatment_name || '');
        setNotesPatient(d.notes_patient_visible || '');
        setNotesInternal(d.notes_internal || '');
        setValidDays(d.valid_days || 14);
        setItems((d.offer_items || []).map(it => ({
          item_type:      it.item_type || 'treatment',
          description:    it.description || '',
          price:          it.price || 0,
          currency:       it.currency || null,
          treatment_slug: it.treatment_slug || null,
          _source:        it._source || null,
        })));
      } catch (err) {
        toast({ title: t('provider.leads.clinicalReview.toastCouldNotLoad'), description: err?.response?.data?.detail?.message || err?.message, variant: 'destructive' });
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [review.clinical_review_id, review.lead_id]);

  const slugToName = (slug) => (catalog.find(c => c.slug === slug)?.name) || slug;
  const totalFromItems = items.reduce((s, it) => s + (parseFloat(it.price) || 0) * (parseInt(it.quantity || 1, 10) || 1), 0);

  const submit = async () => {
    if (!basePrice || parseFloat(basePrice) <= 0) {
      toast({ title: t('provider.leads.clinicalReview.toastBasePriceReq'), variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        source_clinical_review_id: review.clinical_review_id,
        parent_offer_id:           parentOfferId || null,
        treatment_slug:            treatmentSlug || null,
        treatment_name:            treatmentName || null,
        base_price:                parseFloat(basePrice),
        currency,
        valid_days:                parseInt(validDays, 10) || 14,
        notes_patient_visible:     notesPatient,
        notes_internal:            notesInternal,
        hotel_nights:              hotelNights ? parseInt(hotelNights, 10) : null,
        hotel_type:                hotelType || null,
        airport_transfer:          !!airportTransfer,
        offer_items: items.map(it => ({
          item_type:      it.item_type,
          description:    it.description,
          price:          parseFloat(it.price) || 0,
          currency:       it.currency || currency,
          quantity:       1,
          treatment_slug: it.treatment_slug || null,
        })),
      };
      const { data } = await api.post(`/clinic/leads/${review.lead_id}/revised-offers`, body);
      if (data?.success) {
        toast({ title: t('provider.leads.clinicalReview.toastRevisedCreated', { v: data.offer?.version_number }) });
        setCreatedOffer(data.offer);
      }
    } catch (err) {
      toast({ title: t('provider.leads.clinicalReview.toastSubmitFailed'), description: formatOfferError(err, t, err?.message), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const addCatalogItem = (slug, asType) => {
    const name = slugToName(slug);
    setItems(prev => [...prev, {
      item_type: asType, description: name, price: 0, currency,
      treatment_slug: slug, _source: 'sales_added',
    }]);
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="revised-offer-editor">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">{t('provider.leads.clinicalReview.revisedTitle', { no: review.review_no })}</h3>
            <p className="text-xs text-gray-500">{t('provider.leads.clinicalReview.revisedSubtitle')}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="revised-offer-editor-close">{t('provider.leads.clinicalReview.close')}</Button>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-gray-500">{t('provider.leads.clinicalReview.loadingDraft')}</div>
        ) : createdOffer ? (
          <div className="p-6 text-sm space-y-3" data-testid="revised-offer-created-confirmation">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
              <div className="font-medium text-emerald-900">{t('provider.leads.clinicalReview.revisedCreatedHeading', { v: createdOffer.version_number })}</div>
              <div className="text-xs text-emerald-800 mt-1 font-mono">offer_id: {createdOffer.id}</div>
              <div className="text-xs text-emerald-800 font-mono">source_clinical_review_id: {createdOffer.source_clinical_review_id}</div>
              <div className="text-xs text-emerald-800 font-mono">source_review_revision: {createdOffer.source_review_revision}</div>
              <div className="text-xs text-emerald-800 font-mono">parent_offer_id: {createdOffer.parent_offer_id || '— (root)'}</div>
              <div className="text-xs text-emerald-800 font-mono">source_offer_id (chain root): {createdOffer.source_offer_id}</div>
            </div>
            <Button size="sm" onClick={onClose}>{t('provider.leads.clinicalReview.done')}</Button>
          </div>
        ) : (
          <div className="flex flex-1 overflow-hidden">
            {/* LEFT — read-only doctor context */}
            <div className="w-72 border-r bg-slate-50 p-4 overflow-y-auto" data-testid="revised-offer-doctor-context">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{t('provider.leads.clinicalReview.doctorRecommendation')}</div>
              <div className="text-sm font-medium text-slate-800 mb-3">{(review.recommendation || '').replace(/_/g,' ')}</div>

              {(review.recommended_treatments || []).length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{t('provider.leads.clinicalReview.recommended')}</div>
                  <ul className="text-xs text-slate-700 space-y-1 mt-1">
                    {review.recommended_treatments.map(t => (
                      <li key={`rec-${t.slug}`} className="flex items-center justify-between">
                        <span>{t.label || t.slug}</span>
                        <Button size="sm" variant="outline" className="h-5 text-[10px] px-1.5" onClick={() => addCatalogItem(t.slug, 'treatment')} data-testid={`doctor-add-rec-${t.slug}`}>{t('provider.leads.clinicalReview.add')}</Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(review.additional_treatments || []).length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{t('provider.leads.clinicalReview.additional')}</div>
                  <ul className="text-xs text-slate-700 space-y-1 mt-1">
                    {review.additional_treatments.map(t => (
                      <li key={`add-${t.slug}`} className="flex items-center justify-between">
                        <span>{t.label || t.slug}</span>
                        <Button size="sm" variant="outline" className="h-5 text-[10px] px-1.5" onClick={() => addCatalogItem(t.slug, 'add_on')} data-testid={`doctor-add-add-${t.slug}`}>+ add</Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(review.alternative_treatments || []).length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{t('provider.leads.clinicalReview.alternative')}</div>
                  <ul className="text-xs text-slate-700 space-y-1 mt-1">
                    {review.alternative_treatments.map(t => (
                      <li key={`alt-${t.slug}`}>{t.label || t.slug}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(review.removed_or_rejected_treatments || []).length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-wide text-rose-600">{t('provider.leads.clinicalReview.removedOrNotAdvised')}</div>
                  <ul className="text-xs text-rose-700 space-y-1 mt-1">
                    {review.removed_or_rejected_treatments.map(t => (
                      <li key={`rem-${t.slug}`}>{t.label || t.slug}</li>
                    ))}
                  </ul>
                </div>
              )}

              {review.patient_visible_summary && (
                <div className="mt-3 p-2 bg-emerald-50 rounded text-[11px] text-emerald-900">
                  <div className="font-medium mb-0.5">{t('provider.leads.clinicalReview.patientSummaryReadonly')}</div>
                  {review.patient_visible_summary}
                </div>
              )}
              {review.sales_rep_guidance && (
                <div className="mt-2 p-2 bg-amber-50 rounded text-[11px] text-amber-900">
                  <div className="font-medium mb-0.5">{t('provider.leads.clinicalReview.salesGuidanceReadonly')}</div>
                  {review.sales_rep_guidance}
                </div>
              )}
            </div>

            {/* RIGHT — editable commercial offer */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="revised-offer-editor-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.treatment')}</label>
                  <select className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={treatmentSlug} onChange={e => { setTreatmentSlug(e.target.value); setTreatmentName(slugToName(e.target.value)); }} data-testid="revised-offer-treatment-slug">
                    <option value="">{t('provider.leads.clinicalReview.selectOption')}</option>
                    {catalog.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.displayNameOpt')}</label>
                  <input className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={treatmentName} onChange={e => setTreatmentName(e.target.value)} data-testid="revised-offer-treatment-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.basePrice')}</label>
                  <input type="number" min="0" className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={basePrice} onChange={e => setBasePrice(e.target.value)} data-testid="revised-offer-base-price" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.currency')}</label>
                  <select className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={currency} onChange={e => setCurrency(e.target.value)} disabled={!currencyPolicy?.resolved} data-testid="revised-offer-currency">
                    {(currencyPolicy?.resolved ? (currencyPolicy.allowed_currencies || []) : []).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {currencyPolicy && !currencyPolicy.resolved && (
                    <p className="mt-1 text-xs text-red-700" data-testid="revised-offer-currency-policy-blocked">{t(currencyPolicyReasonKey(currencyPolicy.reason))}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.validDays')}</label>
                  <input type="number" min="1" max="365" className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={validDays} onChange={e => setValidDays(e.target.value)} data-testid="revised-offer-valid-days" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.parentOffer')}</label>
                  <select className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={parentOfferId} onChange={e => setParentOfferId(e.target.value)} data-testid="revised-offer-parent">
                    <option value="">{t('provider.leads.clinicalReview.parentNone')}</option>
                    {existingOffers.map(o => <option key={o.id} value={o.id}>v{o.version_number} · {o.treatment_name || o.treatment_slug || o.id.slice(0,8)}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.offerItems')}</label>
                  <span className="text-[11px] text-gray-500">{t('provider.leads.clinicalReview.itemsTotal', { currency, amount: totalFromItems.toFixed(2) })}</span>
                </div>
                <div className="border rounded divide-y">
                  {items.length === 0 && <div className="p-3 text-xs text-gray-500 italic">{t('provider.leads.clinicalReview.noItemsYet')}</div>}
                  {items.map((it, idx) => (
                    <div key={idx} className="p-2 grid grid-cols-12 gap-2 items-center text-sm" data-testid={`revised-offer-item-${idx}`}>
                      <select className="col-span-2 border border-gray-300 rounded px-1.5 py-1 text-xs" value={it.item_type} onChange={e => updateItem(idx, { item_type: e.target.value })}>
                        <option value="treatment">treatment</option>
                        <option value="add_on">add_on</option>
                        <option value="package">package</option>
                        <option value="travel">travel</option>
                      </select>
                      <input className="col-span-4 border border-gray-300 rounded px-1.5 py-1 text-xs" placeholder={t('provider.leads.clinicalReview.descriptionPh')} value={it.description} onChange={e => updateItem(idx, { description: e.target.value })} data-testid={`revised-offer-item-${idx}-desc`} />
                      <select className="col-span-3 border border-gray-300 rounded px-1.5 py-1 text-xs" value={it.treatment_slug || ''} onChange={e => updateItem(idx, { treatment_slug: e.target.value || null, description: e.target.value ? slugToName(e.target.value) : it.description })}>
                        <option value="">{t('provider.leads.clinicalReview.noSlug')}</option>
                        {catalog.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                      </select>
                      <input type="number" min="0" className="col-span-2 border border-gray-300 rounded px-1.5 py-1 text-xs text-right" value={it.price} onChange={e => updateItem(idx, { price: e.target.value })} data-testid={`revised-offer-item-${idx}-price`} />
                      <button onClick={() => removeItem(idx)} className="col-span-1 text-rose-600 text-xs hover:underline" data-testid={`revised-offer-item-${idx}-remove`}>{t('provider.leads.clinicalReview.remove')}</button>
                      {it._source && (
                        <span className="col-span-12 text-[10px] text-slate-500 -mt-1">{t('provider.leads.clinicalReview.sourceLabel', { src: it._source })}</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setItems(prev => [...prev, { item_type: 'add_on', description: '', price: 0, currency, treatment_slug: null, _source: 'sales_added' }])} data-testid="revised-offer-add-blank-item">{t('provider.leads.clinicalReview.addBlankItem')}</Button>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.notesPatient')}</label>
                <textarea rows={2} className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={notesPatient} onChange={e => setNotesPatient(e.target.value)} data-testid="revised-offer-notes-patient" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.notesInternal')}</label>
                <textarea rows={2} className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={notesInternal} onChange={e => setNotesInternal(e.target.value)} data-testid="revised-offer-notes-internal" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.hotelNights')}</label>
                  <input type="number" min="0" className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={hotelNights} onChange={e => setHotelNights(e.target.value)} data-testid="revised-offer-hotel-nights" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">{t('provider.leads.clinicalReview.hotelType')}</label>
                  <input className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm" placeholder={t('provider.leads.clinicalReview.hotelTypePh')} value={hotelType} onChange={e => setHotelType(e.target.value)} data-testid="revised-offer-hotel-type" />
                </div>
                <div className="flex items-end">
                  <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
                    <input type="checkbox" checked={airportTransfer} onChange={e => setAirportTransfer(e.target.checked)} data-testid="revised-offer-airport-transfer" /> {t('provider.leads.clinicalReview.airportTransfer')}
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !createdOffer && (
          <div className="px-4 py-3 border-t flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>{t('provider.leads.clinicalReview.cancel')}</Button>
            <Button size="sm" disabled={submitting || !currencyPolicy?.resolved || !currency} onClick={submit} data-testid="revised-offer-submit">
              {submitting ? t('provider.leads.clinicalReview.creating') : t('provider.leads.clinicalReview.createRevisedOffer')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadManagement;
